import React, { useCallback, useEffect, useMemo, useState } from "react";

import deepmerge from "deepmerge";
import Highcharts, {
  SeriesClickEventObject,
  TooltipFormatterContextObject,
  YAxisOptions,
  YAxisPlotLinesOptions,
} from "highcharts";
import HighchartsReact from "highcharts-react-official";
import add from "lodash-es/add";
import { defineMessages, useIntl } from "react-intl";

import Identified from "@mapmycustomers/shared/types/base/Identified";
import { AnyEntityId } from "@mapmycustomers/shared/types/entity";
import { OutOfCadenceEntity } from "@mapmycustomers/shared/types/entity/activities/OutOfCadenceEntity";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";

import ListModalForCharts from "@app/component/ListModalForCharts";
import ChartType from "@app/scene/home/enum/ChartType";
import { DisplayType } from "@app/scene/home/enum/DisplayType";
import { RecordsStats } from "@app/scene/home/types/RecordsStats";
import { userDisplayNameWithEmail } from "@app/util/formatters";

import styles from "./Chart.module.scss";
import { GroupByType } from "./enum/GroupByType";
import { GroupByEntity } from "./types/GroupByEntity";
import { displayTypeOperations, entityName } from "./util";

const DEFAULT_CHART_OPTIONS: Highcharts.Options = {
  chart: {
    type: "column",
  },
  credits: {
    enabled: false,
  },
  legend: {
    enabled: false,
  },
  plotOptions: {
    column: {
      colorByPoint: true,
    },
  },
  title: {
    text: undefined,
  },
  tooltip: {
    headerFormat: "<small>{point.key}</small><br/>",
    useHTML: true,
  },
  xAxis: {
    labels: {
      style: {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },
  },
  yAxis: {
    title: undefined,
  },
};

const messages = defineMessages({
  overCadencePercent: {
    id: "home.cards.engagement.chart.tooltip.overCadencePercent",
    defaultMessage: "Past due: {percent}%",
    description: "Engagement Card chart tooltip Over cadence % label",
  },
  recordsOverCadence: {
    id: "home.cards.engagement.chart.tooltip.recordsOverCadence",
    defaultMessage: "Records past due",
    description: "Engagement Card chart tooltip Records over cadence label",
  },
  totalRecords: {
    id: "home.cards.engagement.chart.tooltip.totalRecords",
    defaultMessage: "Total records",
    description: "Engagement Card chart tooltip Total records label",
  },
});

const yAxisLabelsFormat: Record<DisplayType, YAxisOptions["labels"]> = {
  [DisplayType.GROSS]: {
    format: "{value}",
  },
  [DisplayType.PERCENT]: {
    format: "{value}%",
  },
};

const CONTAINER_PROPS = {
  style: {
    width: "100%",
  },
};

interface Props {
  displayType: DisplayType;
  groupByEntities: GroupByEntity[];
  groupByType: GroupByType;
  onSelectedData: (data: { id: AnyEntityId; type: GroupByType }, page: number) => void;
  records: OutOfCadenceEntity[];
  recordsOutOfCadence: Record<Identified["id"], RecordsStats>;
  totalRecords: number;
}

const Chart: React.FC<Props> = ({
  displayType,
  groupByEntities,
  groupByType,
  onSelectedData,
  records,
  recordsOutOfCadence,
  totalRecords,
}) => {
  const intl = useIntl();
  const [page, setPage] = useState<number>(1);
  const [selectedData, setSelectedData] = useState<
    { id: AnyEntityId; type: GroupByType } | undefined
  >();

  const availableGroupByEntities = useMemo(
    () => groupByEntities.filter((entity: GroupByEntity) => recordsOutOfCadence[entity.id]),
    [groupByEntities, recordsOutOfCadence]
  );
  useEffect(() => {
    if (selectedData) {
      onSelectedData({ id: selectedData.id, type: selectedData.type }, page);
    }
  }, [selectedData, page, onSelectedData]);

  const handleSelect = useCallback(
    (data) => {
      setSelectedData(data);
    },
    [setSelectedData]
  );

  const handleHide = useCallback(() => {
    setSelectedData(undefined);
    setPage(1);
  }, [setSelectedData, setPage]);

  const selectedEntity = useMemo(() => {
    return availableGroupByEntities.find((entity) => entity.id === selectedData?.id);
  }, [selectedData, availableGroupByEntities]);

  const title = useMemo(() => {
    return (
      <div>
        <h2>
          {intl.formatMessage({
            id: "engagement.listModal.title",
            defaultMessage: "Records past due",
            description: "Records over cadence list modal title",
          })}
        </h2>
        <h6 className={styles.name}>
          {selectedData?.type === GroupByType.USER
            ? `${selectedEntity ? userDisplayNameWithEmail(selectedEntity as User) : ""}`
            : (selectedEntity as Team)?.name}
        </h6>
      </div>
    );
  }, [selectedData, selectedEntity, intl]);

  const chartConfig = useMemo<Highcharts.Options>(() => {
    const names = availableGroupByEntities.map(entityName[groupByType]);

    const itemIds = availableGroupByEntities.map(({ id }) => id);

    const categories = names;

    const { calculate, round } = displayTypeOperations[displayType];
    const availableData = availableGroupByEntities
      .map((entity) => recordsOutOfCadence[entity.id])
      .map(calculate);

    const seriesData = availableData.map((value, i) => ({
      custom: {
        entityTypeId: itemIds[i],
      },
      name: names[i],
      y: round(value),
    }));

    const average = seriesData.length && availableData.reduce(add, 0) / seriesData.length;

    const averageFormatted = round(average);
    const plotLines: YAxisPlotLinesOptions[] = average
      ? [
          {
            color: "red",
            dashStyle: "LongDash",
            value: averageFormatted,
            width: 1,
            zIndex: 1,
          },
        ]
      : [];

    const result: Highcharts.Options = deepmerge.all([
      {},
      DEFAULT_CHART_OPTIONS,
      {
        series: [
          {
            data: seriesData,
            events: {
              click: function (event: SeriesClickEventObject) {
                const data = {
                  id: event.point.options.custom?.entityTypeId,
                  type: groupByType,
                };
                handleSelect(data);
              },
            },
            type: "column",
          },
        ],
        tooltip: {
          formatter(this: TooltipFormatterContextObject): string {
            const { color, point } = this;

            const availableGroupByEntity = availableGroupByEntities[point.index];
            const name = names[point.index];
            const records = recordsOutOfCadence[availableGroupByEntity.id];
            const { calculate, round } = displayTypeOperations[DisplayType.PERCENT];
            const percent = round(calculate(records));

            return `<span style="color:${color};">${name}</span><br>
            <small>${intl.formatMessage(messages.totalRecords)}: ${records.total}</small><br>
            <small>${intl.formatMessage(messages.recordsOverCadence)}: ${
              records.relevant
            }</small><br>
            <small>${intl.formatMessage(messages.overCadencePercent, { percent })}</small><br>
`;
          },
        },
        xAxis: {
          categories: categories,
        },
        yAxis: {
          labels: {
            ...yAxisLabelsFormat[displayType],
          },
          plotLines,
        },
      },
    ]);
    return result;
  }, [availableGroupByEntities, displayType, intl, groupByType, recordsOutOfCadence, handleSelect]);

  return (
    <>
      <HighchartsReact
        containerProps={CONTAINER_PROPS}
        highcharts={Highcharts}
        isPureConfig
        options={chartConfig}
      />
      {selectedData && (
        <ListModalForCharts
          chartType={ChartType.ENGAGEMENT}
          onHide={handleHide}
          onPageChange={setPage}
          page={page}
          pageSize={25}
          records={records}
          title={title}
          totalRecords={totalRecords}
        />
      )}
    </>
  );
};

export default React.memo(Chart);
